import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  toggle_lease_penalty_status,
  update_lease_penalty
} from "../../../../utils/urls";

const EditPenalty = ({
  penalty,
  isDialog = false,
  onSuccess,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [penaltyData, setPenaltyData] = useState({
    name: "",
    type: "percentage",
    effectDays: "1",
    percentage: "",
    amount: "",
    isActive: true
  });

  useEffect(() => {
    if (penalty) {
      setPenaltyData({
        name: penalty.name || "",
        type: penalty.type || "percentage",
        effectDays: penalty.effectDays?.toString() || "1",
        percentage: penalty.type === 'percentage' ? penalty.percentage?.toString() || "" : "",
        amount: penalty.type === 'fixed' ? penalty.amount?.toString() || "" : "",
        isActive: penalty.isActive !== undefined ? penalty.isActive : true
      });
    }
  }, [penalty]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPenaltyData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!penaltyData.name.trim()) {
      toastify("Penalty name is required", "error");
      return false;
    }

    if (penaltyData.type === "percentage") {
      if (!penaltyData.percentage ||
        isNaN(penaltyData.percentage) ||
        Number(penaltyData.percentage) < 0 ||
        Number(penaltyData.percentage) > 100) {
        toastify("Please enter a valid percentage between 0 and 100", "error");
        return false;
      }
    }

    if (penaltyData.type === "fixed") {
      if (!penaltyData.amount ||
        isNaN(penaltyData.amount) ||
        Number(penaltyData.amount) < 0) {
        toastify("Please enter a valid positive amount", "error");
        return false;
      }
    }

    if (![1, 3, 7].includes(Number(penaltyData.effectDays))) {
      toastify("Effect days must be either 1, 3, or 7", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm() || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found", "error");
        return;
      }

      const formData = {
        name: penaltyData.name.trim(),
        type: penaltyData.type,
        effectDays: Number(penaltyData.effectDays),
        ...(penaltyData.type === "percentage"
          ? { percentage: Number(penaltyData.percentage) }
          : { amount: Number(penaltyData.amount) }
        )
      };

      const response = await makeRequest2(
        `${update_lease_penalty}/${facilityId}/${penalty._id}`,
        "PUT",
        formData
      );

      if (response.success) {
        toastify("Penalty updated successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastify(response.error || "Failed to update penalty", "error");
      }
    } catch (error) {
      console.error("Error updating penalty:", error);
      toastify(error.message || "An unexpected error occurred", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found", "error");
        return;
      }

      const response = await makeRequest2(
        `${toggle_lease_penalty_status}/${facilityId}/${penalty._id}`,
        "PATCH",
        { isActive: !penaltyData.isActive }
      );

      if (response.success) {
        setPenaltyData(prev => ({
          ...prev,
          isActive: !prev.isActive
        }));
        toastify(
          `Penalty ${!penaltyData.isActive ? 'activated' : 'deactivated'} successfully`,
          "success"
        );
      } else {
        toastify(response.error || "Failed to update penalty status", "error");
      }
    } catch (error) {
      console.error("Error toggling penalty status:", error);
      toastify(error.message || "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!penalty) {
    return <div>Loading...</div>;
  }

  return (
    <div className="card-body">
      {isDialog && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">{penalty.name}</h6>
            <span className={`badge ${penaltyData.isActive ? 'bg-success' : 'bg-secondary'}`}>
              {penaltyData.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <Button
            type="button"
            className={`btn btn-sm ${penaltyData.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
            onClick={handleToggleStatus}
            loading={loading}
            disabled={loading || isSubmitting}
          >
            <i className={`ti ${penaltyData.isActive ? 'ti-ban' : 'ti-check'} me-1`}></i>
            {penaltyData.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      )}

      <div className="row">
        <div className="col-sm-6">
          <label className="form-label">Penalty Name *</label>
          <InputText
            className="form-control"
            name="name"
            value={penaltyData.name}
            onChange={handleInputChange}
            placeholder="Enter penalty name"
            disabled={isSubmitting}
          />
        </div>

        <div className="col-sm-6">
          <label className="form-label">Penalty Type *</label>
          <select
            className="form-control"
            name="type"
            value={penaltyData.type}
            onChange={handleInputChange}
            disabled={isSubmitting}
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        {penaltyData.type === "percentage" ? (
          <div className="col-sm-6 mt-3">
            <label className="form-label">Percentage (%) *</label>
            <InputText
              type="number"
              className="form-control"
              name="percentage"
              value={penaltyData.percentage}
              onChange={handleInputChange}
              placeholder="Enter percentage"
              min="0"
              max="100"
              step="0.01"
              disabled={isSubmitting}
            />
            <small className="text-muted">Enter a value between 0 and 100</small>
          </div>
        ) : (
          <div className="col-sm-6 mt-3">
            <label className="form-label">Fixed Amount *</label>
            <InputText
              type="number"
              className="form-control"
              name="amount"
              value={penaltyData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              min="0"
              step="0.01"
              disabled={isSubmitting}
            />
            <small className="text-muted">Enter a positive amount</small>
          </div>
        )}

        <div className="col-sm-6 mt-3">
          <label className="form-label">Effect Days *</label>
          <select
            className="form-control"
            name="effectDays"
            value={penaltyData.effectDays}
            onChange={handleInputChange}
            disabled={isSubmitting}
          >
            <option value="1">1 Day</option>
            <option value="3">3 Days</option>
            <option value="7">7 Days</option>
          </select>
          <small className="text-muted">Number of days after which penalty applies</small>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4">
        {onClose && (
          <Button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || loading}
        >
          {isSubmitting ? "Updating..." : "Update Penalty"}
        </Button>
      </div>
    </div>
  );
};

export default EditPenalty;